// What every frame does as pages change: the page's tab kept in view, and focus moved to the page, so a screen reader
// starts there as it would on a new page.
import { useEffect, useRef, type RefObject } from 'react';
import { useLocation } from 'react-router';

/** Keeps the current tab of a row that scrolls sideways in view. */
export function useTabInView(): RefObject<HTMLElement | null> {
  const tabs = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    tabs.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [pathname]);
  return tabs;
}

/** Moves focus to the page's main part when the page changes, but not when the portal first opens. */
export function useFocusOnNavigation(): void {
  const { pathname } = useLocation();
  // The page it was on, not a "first time" flag: React runs effects twice in development.
  const last = useRef(pathname);
  useEffect(() => {
    if (last.current !== pathname) {
      last.current = pathname;
      document.getElementById('main')?.focus({ preventScroll: true });
    }
  }, [pathname]);
}
